import api from '@/lib/axios';

import type { PaginatedResponse } from '@/types/tenant';
import type {
  EmployeeDto,
  EmployeeStatusCounts,
  SaveEmployeePayload,
  UpdateEmployeePayload,
} from '@/types/employee';
import type { ListFilterPayload } from '@/types/filters';

// ============================================
// Query Params
// ============================================

interface GetEmployeesParams {
  page?: number;
  size?: number;
  sort?: string;
}

// ============================================
// Employees CRUD
// ============================================
// Responses are the uniform envelope; the axios interceptor unwraps `data`,
// so `response.data` is already the payload.

export const getEmployees = async (
  params: GetEmployeesParams = {},
  body?: ListFilterPayload,
): Promise<PaginatedResponse<EmployeeDto>> => {
  const response = await api.post<PaginatedResponse<EmployeeDto>>(
    '/employee-service/v1/employees/list',
    body ?? {},
    { params },
  );
  return response.data;
};

export const getEmployeeById = async (id: string): Promise<EmployeeDto> => {
  const response = await api.get<EmployeeDto>(`/employee-service/v1/employees/${id}`);
  return response.data;
};

export const createEmployee = async (
  data: SaveEmployeePayload,
): Promise<EmployeeDto> => {
  const response = await api.post<EmployeeDto>('/employee-service/v1/employees', data, {
    successToast: true,
  });
  return response.data;
};

export const updateEmployee = async (
  id: string,
  data: UpdateEmployeePayload,
): Promise<EmployeeDto> => {
  const response = await api.put<EmployeeDto>(
    `/employee-service/v1/employees/${id}`,
    data,
    { successToast: true },
  );
  return response.data;
};

export const deleteEmployee = async (id: string): Promise<void> => {
  await api.delete(`/employee-service/v1/employees/${id}`, { successToast: true });
};

export const offboardEmployee = async (
  id: string,
  inactivateUser = true,
): Promise<EmployeeDto> => {
  const response = await api.post<EmployeeDto>(
    `/employee-service/v1/employees/${id}/offboard`,
    undefined,
    { params: { inactivateUser }, successToast: true },
  );
  return response.data;
};

export const activateEmployee = async (id: string): Promise<EmployeeDto> => {
  const response = await api.post<EmployeeDto>(
    `/employee-service/v1/employees/${id}/activate`,
    undefined,
    { successToast: true },
  );
  return response.data;
};

export const provisionEmployeeAccess = async (id: string): Promise<EmployeeDto> => {
  const response = await api.post<EmployeeDto>(
    `/employee-service/v1/employees/${id}/provision-access`,
    undefined,
    { successToast: true },
  );
  return response.data;
};

export const requestEmployeeActivation = async (id: string): Promise<EmployeeDto> => {
  const response = await api.post<EmployeeDto>(
    `/employee-service/v1/employees/${id}/request-activation`,
    undefined,
    { successToast: true },
  );
  return response.data;
};

export const getEmployeeCounts = async (): Promise<EmployeeStatusCounts> => {
  const response = await api.get<EmployeeStatusCounts>(
    '/employee-service/v1/employees/counts',
  );
  return response.data;
};

/** Find employee by email — used to resolve employee ID from session */
export const findEmployeeByEmail = async (email: string): Promise<EmployeeDto | null> => {
  const response = await api.post<PaginatedResponse<EmployeeDto>>(
    '/employee-service/v1/employees/list',
    { search: email },
    { params: { size: 1 } },
  );
  const content = response.data.content;
  return content[0] ?? null;
};
